import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import adminApi from './api'
import { Notification } from './components/Notification'
import { ConfirmModal } from './components/ConfirmModal'
import { API_URL } from '../api/base'


interface EventName {
  ru?: string
  ky?: string
  en?: string
}

interface MediaCaption {
  ru?: string
  ky?: string
  en?: string
}

interface MediaFile {
  id: number
  event_id: number | null
  type: 'image' | 'video'
  url: string
  thumbnail: string | null
  caption: string | MediaCaption | null
  display_order: number
  created_at: string
  event_name: string | EventName | null
  event_slug: string | null
}

interface EventOption {
  id: number
  name: string | EventName
}

export const MediaAdmin: React.FC = () => {
  const navigate = useNavigate()
  const [media, setMedia] = useState<MediaFile[]>([])
  const [events, setEvents] = useState<EventOption[]>([])
  const [loading, setLoading] = useState(true)
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<{show: boolean, id: number | null}>({show: false, id: null})
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 })
  const [editMedia, setEditMedia] = useState<{ item: MediaFile; eventId: string } | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [userRole, setUserRole] = useState<string>('')

  useEffect(() => {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        setUserRole(user.role || '')
      } catch {
        // ignore
      }
    }
  }, [])

  useEffect(() => {
    fetchMedia()
    fetchEvents()
  }, [])

  useEffect(() => {
    if (openMenuId === null) return
    const close = () => setOpenMenuId(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [openMenuId])

  const fetchEvents = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/events')
      setEvents((data.events || []).map((e: any) => ({ id: e.id, name: e.name })))
    } catch (err) {
      console.error('Error fetching events:', err)
    }
  }

  const fetchMedia = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/media')
      setMedia(data.media || [])
    } catch (err) {
      console.error('Error fetching media:', err)
      setNotification({ message: 'Ошибка загрузки медиа', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const deleteMedia = (id: number) => {
    setConfirmDelete({show: true, id})
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return

    try {
      await adminApi.delete(`/api/admin/media/${confirmDelete.id}`)
      setNotification({ message: 'Медиа файл удален', type: 'success' })
      fetchMedia()
    } catch (err) {
      console.error('Error deleting media:', err)
      setNotification({ message: 'Ошибка при удалении', type: 'error' })
    } finally {
      setConfirmDelete({show: false, id: null})
    }
  }

  const openEdit = (item: MediaFile) => {
    setEditMedia({ item, eventId: item.event_id?.toString() || '' })
    setOpenMenuId(null)
  }

  const saveEdit = async () => {
    if (!editMedia) return
    if (!editMedia.eventId) {
      setNotification({ message: 'Выберите событие', type: 'error' })
      return
    }
    setSavingEdit(true)
    try {
      await adminApi.put(`/api/admin/media/${editMedia.item.id}`, {
        eventId: parseInt(editMedia.eventId, 10),
      })
      setNotification({ message: 'Событие обновлено', type: 'success' })
      setEditMedia(null)
      fetchMedia()
    } catch (err: any) {
      setNotification({ message: err?.response?.data?.message || 'Ошибка сохранения', type: 'error' })
    } finally {
      setSavingEdit(false)
    }
  }

  const saveOrder = async () => {
    try {
      const mediaIds = media.map(m => m.id)

      await adminApi.put(
        '/api/admin/media/reorder',
        { mediaIds }
      )

      setHasChanges(false)
      setNotification({ message: 'Порядок сохранен', type: 'success' })
    } catch (err) {
      console.error('Error saving order:', err)
      setNotification({ message: 'Ошибка при сохранении', type: 'error' })
    }
  }

  // Drag and drop handlers for media list
  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newMedia = [...media]
    const draggedItem = newMedia[draggedIndex]

    newMedia.splice(draggedIndex, 1)
    newMedia.splice(index, 0, draggedItem)

    setMedia(newMedia)
    setDraggedIndex(index)
    setHasChanges(true)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const getEventName = (eventName: string | EventName | null) => {
    if (!eventName) return '-'
    if (typeof eventName === 'object' && eventName.ru) {
      return eventName.ru
    }
    return String(eventName)
  }

  const getMediaThumbnail = (item: MediaFile) => {
    if (item.type === 'image') {
      return `${API_URL}${item.url}`
    }
    if (item.type === 'video' && item.thumbnail) {
      return item.thumbnail
    }
    return null
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Медиа Файлы</h1>
          <p className="page-subtitle">Управление изображениями и видео для событий</p>
        </div>
        <div className="flex gap-3">
          {hasChanges && (
            <button
              onClick={saveOrder}
              className="btn-primary bg-[#52c41a]"
            >
              Сохранить порядок
            </button>
          )}
          <button
            onClick={() => navigate('/admin/media/new')}
            className="btn-primary"
          >
            Загрузить медиа
          </button>
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : media.length === 0 ? (
        <div className="table-card px-10 py-20 text-center">
          <div className="mb-6">
            <div className="text-[64px] mb-4">📁</div>
            <div className="empty-state-text text-base mb-2">
              Медиа файлы не найдены
            </div>
            <div className="text-sm text-black/45">
              Загрузите изображения или добавьте видео для событий
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/media/new')}
            className="btn-primary"
          >
            Загрузить медиа
          </button>
        </div>
      ) : (
        <div
          className="grid gap-5"
          // dynamic: auto-fill grid without Tailwind's fixed cols API
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
        >
          {media.map((item, index) => (
            <div
              key={item.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`admin-card bg-white rounded-lg overflow-hidden cursor-move transition-all ${
                draggedIndex === index
                  ? 'border-2 border-dashed border-[#1890ff] shadow-[0_4px_12px_rgba(24,144,255,0.3)]'
                  : 'border border-[#f0f0f0] shadow-sm'
              }`}
            >
              {/* Thumbnail */}
              <div className="w-full h-[200px] bg-[#f5f5f5] flex items-center justify-center overflow-hidden relative">
                {getMediaThumbnail(item) ? (
                  <img
                    src={getMediaThumbnail(item)!}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-500">Нет превью</span>
                )}
                <div className="absolute top-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                  #{item.display_order + 1}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
                    setOpenMenuId(openMenuId === item.id ? null : item.id)
                  }}
                  className="absolute top-2 right-2 bg-black/60 text-white border-0 w-8 h-8 rounded-md text-xl cursor-pointer flex items-center justify-center leading-none"
                  aria-label="Действия"
                >
                  ⋮
                </button>
              </div>

              {/* Info */}
              <div className="px-3.5 py-3">
                <div className="text-[13px] text-black/75 mb-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  {getEventName(item.event_name)}
                </div>
                <div className="text-xs text-black/45">
                  {new Date(item.created_at).toLocaleDateString('ru-RU')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {openMenuId !== null && (() => {
        const item = media.find(m => m.id === openMenuId)
        if (!item) return null
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] min-w-[180px] overflow-hidden"
            // dynamic: menu position computed from trigger rect
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <button
              onClick={() => openEdit(item)}
              className="w-full text-left px-3.5 py-2.5 bg-transparent border-0 cursor-pointer text-sm text-gray-800"
            >
              Редактировать
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => {
                  setOpenMenuId(null)
                  deleteMedia(item.id)
                }}
                className="w-full text-left px-3.5 py-2.5 bg-transparent border-0 cursor-pointer text-sm text-[#dc2626] border-t border-gray-100"
              >
                Удалить
              </button>
            )}
          </div>
        )
      })()}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить этот медиа файл? Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({show: false, id: null})}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />

      {editMedia && (
        <div
          onClick={() => !savingEdit && setEditMedia(null)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-6 max-w-[480px] w-full shadow-2xl"
          >
            <h3 className="m-0 mb-4 text-lg font-semibold">
              Редактировать медиа
            </h3>

            <div className="mb-5">
              {editMedia.item.type === 'image' && (
                <img
                  src={`${API_URL}${editMedia.item.url}`}
                  alt=""
                  className="w-full max-h-[200px] object-cover rounded-lg mb-4"
                />
              )}

              <label className="block mb-2 text-sm font-medium text-gray-800">
                Связанное событие
              </label>
              <select
                value={editMedia.eventId}
                onChange={(e) => setEditMedia({ ...editMedia, eventId: e.target.value })}
                disabled={savingEdit}
                className="form-control w-full"
              >
                <option value="">— Выберите событие —</option>
                {events.map(ev => {
                  const label = typeof ev.name === 'string' ? ev.name : (ev.name?.ru || ev.name?.ky || ev.name?.en || `#${ev.id}`)
                  return (
                    <option key={ev.id} value={ev.id}>{label}</option>
                  )
                })}
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditMedia(null)}
                disabled={savingEdit}
                className="btn-secondary px-4 py-2"
              >
                Отмена
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                className="btn-primary px-4 py-2"
              >
                {savingEdit ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
